/* eslint-disable */
import Telemetry from './telemetry';

/**
 * A function that wraps a controller (that implements a number of delegates) and wraps each
 * function invocation with another function that logs the caller and parameters and then invokes
 * the original call.
 *
 * Example usage:
 * -  instancesDelegate: telemetryDelegateWrapper(InstancesController.name, ['*'], instancesDelegate) };
 *
 * @param componentName The name of the component - this will be supplied to the Telemetry system.
 * @param propNames The function names that should be intercepted, or a single array item with a wildcard for every function.
 * @param instance The instance of the controller that should be wrapped.
 */
export function telemetryDelegateWrapper<T extends object>(
  componentName: string,
  propNames: string[],
  instance: T | undefined
): T | undefined {
  if (!instance) {
    return undefined;
  }

  const prototype = Object.getPrototypeOf(instance);

  for (const key of Object.getOwnPropertyNames(prototype)) {
    if (!propNames.includes('*') && !propNames.includes(key)) {
      continue;
    }

    const original = prototype[key];

    if (typeof original !== 'function' || key === 'constructor') {
      continue;
    }

    Object.defineProperty(instance, key, {
      value: function (...args: any[]) {
        Telemetry.log({
          eventName: 'wrapped-function-invocation',
          eventSource: `${componentName}.${key}`,
          eventDescription: `Invoked function "${key}" on instance of controller "${componentName}"`,
          eventData: {
            args,
          },
        });

        return original.apply(this, args);
      },
      writable: true,
      configurable: true,
    });
  }

  console.debug('[telemetryDelegateWrapper] wrapped functions for %s...', componentName);

  return instance;
}
